import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PAYMENT_FLAG_HINTS,
  PAYMENT_FLAG_LABELS,
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUSES,
  centsToPesos,
  type PaymentFlag,
  type PaymentStatus,
} from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Badge, Button, Card, ErrorBanner, Modal, Spinner, inputCls } from '../../components/ui';
import { Icon } from '../../components/Icon';

/**
 * Phase 2G.6 — Payment Tracking & Cash Accountability.
 *
 * A pure read of the admin-only /api/payments endpoints: what was
 * recorded as collected, what is still outstanding, per branch, date and
 * payment method, with factual findings per transaction and a payment
 * history drill-down.
 *
 * Deliberately factual: payments are recorded once at receiving, so this
 * view never invents a payment, never marks anything paid from the
 * fulfillment status (DONE/RELEASED does NOT prove collection), never
 * counts a release snapshot as a collection, and never discards an
 * overpayment silently. Missing attribution/method shows "Not recorded";
 * findings are recorded facts, never accusations.
 */

interface PaymentActorDto {
  id: string;
  name: string;
}

interface PaymentRowDto {
  id: string;
  number: string;
  branch: { id: string; code: string };
  customerName: string;
  workflowStatus: string;
  workflowStatusLabel: string;
  fulfillmentMethod: string;
  totalCents: number;
  amountPaidCents: number;
  /** Signed: negative means the recorded payment exceeds the total. */
  outstandingCents: number;
  overpaidCents: number;
  paymentStatus: PaymentStatus;
  paymentStatusLabel: string;
  paymentMethod: string | null;
  paymentRecordedAt: string;
  recordedBy: PaymentActorDto | null;
  releasedAt: string | null;
  flags: PaymentFlag[];
}

interface PaymentsListResponse {
  total: number;
  limit: number;
  offset: number;
  timeZone: string;
  range: { from: string | null; to: string | null } | null;
  status: PaymentStatus | null;
  flag: PaymentFlag | null;
  items: PaymentRowDto[];
}

interface CollectionsTotalsDto {
  transactions: number;
  payments: number;
  totalCents: number;
  collectedCents: number;
  outstandingCents: number;
  overpaidCents: number;
}

interface CollectionsResponse {
  timeZone: string;
  range: { from: string | null; to: string | null } | null;
  generatedAt: string;
  totals: CollectionsTotalsDto;
  byBranch: (CollectionsTotalsDto & { branch: { id: string; code: string } })[];
  byMethod: { method: string | null; payments: number; collectedCents: number }[];
}

interface PaymentHistoryResponse {
  timeZone: string;
  transaction: PaymentRowDto;
  payments: {
    id: string;
    at: string;
    cents: number;
    method: string | null;
    recordedBy: PaymentActorDto | null;
    source: string;
  }[];
  release: {
    at: string;
    paymentStatus: string;
    amountPaidCents: number;
    totalCents: number;
    releasedBy: { id: string | null; name: string; role: string } | null;
  } | null;
}

const PAGE_SIZE = 20;

const STATUS_TONE: Record<PaymentStatus, 'slate' | 'emerald' | 'amber' | 'rose'> = {
  UNPAID: 'rose',
  PARTIAL: 'amber',
  PAID: 'emerald',
  OVERPAID: 'rose',
};

const FLAG_TONE: Record<PaymentFlag, 'slate' | 'amber' | 'rose'> = {
  OUTSTANDING: 'amber',
  NO_PAYMENT: 'rose',
  PARTIAL_PAYMENT: 'amber',
  OVERPAID: 'rose',
  METHOD_NOT_RECORDED: 'slate',
  ATTRIBUTION_MISSING: 'slate',
  RELEASE_SNAPSHOT_MISMATCH: 'rose',
};

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Manila calendar date for the "Today" shortcut (server re-parses it as Manila). */
function manilaToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila' }).format(new Date());
}

const NOT_RECORDED = <span className="italic text-slate-400">Not recorded</span>;

function StatusBadge({ status }: { status: PaymentStatus }) {
  return <Badge tone={STATUS_TONE[status]}>{PAYMENT_STATUS_LABELS[status]}</Badge>;
}

function BalanceCell({ cents }: { cents: number }) {
  if (cents === 0) return <span className="tabular-nums text-slate-500">{centsToPesos(0)}</span>;
  if (cents < 0) {
    return (
      <span className="font-semibold tabular-nums text-rose-700" title="Recorded payment exceeds the total">
        {centsToPesos(cents)}
      </span>
    );
  }
  return <span className="font-semibold tabular-nums text-amber-700">{centsToPesos(cents)}</span>;
}

function FlagBadges({ flags }: { flags: PaymentFlag[] }) {
  if (flags.length === 0) return <span className="text-slate-400">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {flags.map((flag) => (
        <span key={flag} title={PAYMENT_FLAG_HINTS[flag]}>
          <Badge tone={FLAG_TONE[flag]}>{PAYMENT_FLAG_LABELS[flag]}</Badge>
        </span>
      ))}
    </span>
  );
}

export function PaymentsPage() {
  const { branchFilter, setBranchFilter, branches } = useAuth();

  const [collections, setCollections] = useState<CollectionsResponse | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [list, setList] = useState<PaymentsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // View filters — every one is re-validated server-side on each request.
  const [statusFilter, setStatusFilter] = useState<'' | PaymentStatus>('');
  const [flagFilter, setFlagFilter] = useState<'' | PaymentFlag>('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [qFilter, setQFilter] = useState('');
  const [page, setPage] = useState(0);

  // Payment history modal (transaction-level drill-down).
  const [historyRef, setHistoryRef] = useState<string | null>(null);
  const [history, setHistory] = useState<PaymentHistoryResponse | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const scopeParams = () => {
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (fromFilter) params.set('from', fromFilter);
    if (toFilter) params.set('to', toFilter);
    return params;
  };

  // Collection summary: branch + Manila dates only (the reconciliation view).
  useEffect(() => {
    let cancelled = false;
    const params = scopeParams();
    void api
      .get<CollectionsResponse>(`/payments/collections?${params.toString()}`)
      .then((res) => {
        if (!cancelled) {
          setCollections(res);
          setSummaryError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setSummaryError(
            err instanceof ApiError ? err.message : 'Failed to load the collection summary.',
          );
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchFilter, fromFilter, toFilter, refreshKey]);

  // Monitoring list (paginated, deterministic: recorded time desc).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = scopeParams();
    if (statusFilter) params.set('status', statusFilter);
    if (flagFilter) params.set('flag', flagFilter);
    if (qFilter) params.set('q', qFilter);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(page * PAGE_SIZE));
    void api
      .get<PaymentsListResponse>(`/payments?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        setList(res);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load payment records.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchFilter, fromFilter, toFilter, statusFilter, flagFilter, qFilter, page, refreshKey]);

  // Payment history (fetched only when its modal is open).
  useEffect(() => {
    if (!historyRef) {
      setHistory(null);
      setHistoryError(null);
      return;
    }
    let cancelled = false;
    setHistory(null);
    setHistoryError(null);
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    void api
      .get<PaymentHistoryResponse>(
        `/payments/transactions/${encodeURIComponent(historyRef)}?${params.toString()}`,
      )
      .then((res) => {
        if (!cancelled) setHistory(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setHistoryError(err instanceof ApiError ? err.message : 'Failed to load payment history.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [historyRef, branchFilter, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const resetPageAnd = (fn: () => void) => {
    setPage(0);
    fn();
  };

  const scopeLabel = branchFilter === 'ALL' ? 'All branches' : `Branch ${branchFilter}`;
  const total = list?.total ?? 0;
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min((page + 1) * PAGE_SIZE, total);
  const hasListFilters = Boolean(statusFilter || flagFilter || qFilter || fromFilter || toFilter);

  return (
    <section className="space-y-4" aria-label="Payments and collections">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Payments &amp; Collections
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {scopeLabel} · recorded collections and outstanding customer balances — read-only,
            from the payment records the system already keeps.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={refresh}>
            <Icon name="refresh" className="h-5 w-5" />
            Refresh
          </Button>
        </div>
      </div>

      <Card className="flex items-start gap-3 p-4">
        <Icon name="shield" className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
        <p className="text-sm text-slate-500">
          These are <span className="font-semibold text-slate-700">facts, not accusations</span>{' '}
          — payments are recorded once at receiving (method + amount), so collections are dated by
          when they were recorded (Asia/Manila), and a release snapshot is never counted as a
          collection. <span className="font-semibold text-slate-700">DONE or RELEASED does not
          mean paid</span>: payment status is derived from recorded amounts only, never from
          fulfillment. Overpayments are disclosed, never discarded; missing attribution or method
          shows{' '}
          <span className="italic">Not recorded</span> instead of being guessed. This system stores
          no payment reference, so duplicate-reference checks are not possible — and no payment
          deadline or penalty is ever invented.
        </p>
      </Card>

      {error && <ErrorBanner message={error} />}
      {summaryError && <ErrorBanner message={summaryError} />}

      {/* Filters — branch mirrors the header switcher; dates/status/flag/q re-validated server-side. */}
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <label className="flex min-w-44 flex-col gap-1 text-xs font-semibold text-slate-500">
          Branch
          <select
            value={branchFilter}
            onChange={(e) => resetPageAnd(() => setBranchFilter(e.target.value))}
            className={inputCls}
          >
            <option value="ALL">All Branches</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.code}>
                {branch.code} — {branch.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-44 flex-col gap-1 text-xs font-semibold text-slate-500">
          Payment status
          <select
            value={statusFilter}
            onChange={(e) =>
              resetPageAnd(() => setStatusFilter(e.target.value as '' | PaymentStatus))
            }
            className={inputCls}
          >
            <option value="">All statuses</option>
            {PAYMENT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {PAYMENT_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-52 flex-col gap-1 text-xs font-semibold text-slate-500">
          Finding
          <select
            value={flagFilter}
            onChange={(e) => resetPageAnd(() => setFlagFilter(e.target.value as '' | PaymentFlag))}
            className={inputCls}
          >
            <option value="">All findings</option>
            {(Object.keys(PAYMENT_FLAG_LABELS) as PaymentFlag[]).map((flag) => (
              <option key={flag} value={flag}>
                {PAYMENT_FLAG_LABELS[flag]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-40 flex-col gap-1 text-xs font-semibold text-slate-500">
          From
          <input
            type="date"
            value={fromFilter}
            onChange={(e) => resetPageAnd(() => setFromFilter(e.target.value))}
            className={inputCls}
          />
        </label>
        <label className="flex min-w-40 flex-col gap-1 text-xs font-semibold text-slate-500">
          To
          <input
            type="date"
            value={toFilter}
            onChange={(e) => resetPageAnd(() => setToFilter(e.target.value))}
            className={inputCls}
          />
        </label>
        <Button
          variant="secondary"
          onClick={() =>
            resetPageAnd(() => {
              setFromFilter(manilaToday());
              setToFilter(manilaToday());
            })
          }
        >
          Today
        </Button>
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            resetPageAnd(() => setQFilter(searchInput.trim()));
          }}
        >
          <label className="flex min-w-56 flex-col gap-1 text-xs font-semibold text-slate-500">
            Transaction or customer
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="LH1-000001"
              className={inputCls}
            />
          </label>
          <Button variant="secondary" type="submit">
            <Icon name="search" className="h-5 w-5" />
            Search
          </Button>
        </form>
        {hasListFilters && (
          <Button
            variant="secondary"
            onClick={() =>
              resetPageAnd(() => {
                setStatusFilter('');
                setFlagFilter('');
                setFromFilter('');
                setToFilter('');
                setSearchInput('');
                setQFilter('');
              })
            }
          >
            Clear filters
          </Button>
        )}
        <span className="ml-auto text-xs font-medium text-slate-400">
          Dates are Manila business dates (Asia/Manila), applied to when the payment was recorded
          {list?.range ? ` · ${list.range.from ?? '…'} → ${list.range.to ?? '…'}` : ''}
        </span>
      </Card>

      {/* Collection summary — branch + date filters only, one count per transaction. */}
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold text-slate-700">Collection summary</h2>
          {collections && (
            <p className="text-xs text-slate-500">
              {collections.range
                ? `${collections.range.from ?? '…'} → ${collections.range.to ?? '…'} · `
                : 'All recorded history · '}
              {collections.timeZone} · status/finding/search filters apply to the list below only
            </p>
          )}
        </div>
        {!collections && summaryError ? null : !collections ? (
          <div className="flex items-center gap-3 py-6 text-sm text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading collections…
          </div>
        ) : (
          <>
            <div className="mt-2 grid grid-cols-4 gap-3">
              {[
                { label: 'Recorded value', value: collections.totals.totalCents, tone: 'text-slate-900' },
                { label: 'Collected', value: collections.totals.collectedCents, tone: 'text-emerald-700' },
                {
                  label: 'Outstanding',
                  value: collections.totals.outstandingCents,
                  tone: collections.totals.outstandingCents > 0 ? 'text-amber-700' : 'text-slate-900',
                },
              ].map((tile) => (
                <Card key={tile.label} className="p-4">
                  <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                    {tile.label}
                  </p>
                  <p className={`mt-1 text-lg font-bold tabular-nums ${tile.tone}`}>
                    {centsToPesos(tile.value)}
                  </p>
                </Card>
              ))}
              <Card className="p-4">
                <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                  Payments recorded
                </p>
                <p className="mt-1 text-lg font-bold tabular-nums text-slate-900">
                  {collections.totals.payments}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  of {collections.totals.transactions} transaction
                  {collections.totals.transactions === 1 ? '' : 's'} in scope
                </p>
              </Card>
            </div>

            {collections.totals.overpaidCents > 0 && (
              <Card className="mt-3 flex items-start gap-3 border-rose-200 bg-rose-50 p-4">
                <Icon name="alert" className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
                <p className="text-sm text-rose-800">
                  <span className="font-semibold">Overpaid amounts detected:{' '}</span>
                  {centsToPesos(collections.totals.overpaidCents)} above recorded totals. Outstanding
                  is shown net of overpayments; nothing is discarded — filter “Payment exceeds
                  total” below to inspect the affected transactions.
                </p>
              </Card>
            )}

            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-panel shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                    <tr>
                      <th className="px-4 py-3">By branch</th>
                      <th className="px-3 py-3 text-right">Txns</th>
                      <th className="px-3 py-3 text-right">Payments</th>
                      <th className="px-3 py-3 text-right">Value</th>
                      <th className="px-3 py-3 text-right">Collected</th>
                      <th className="px-4 py-3 text-right">Outstanding</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {collections.byBranch.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-4 text-center text-slate-400">
                          No transactions in this scope.
                        </td>
                      </tr>
                    ) : (
                      collections.byBranch.map((row) => (
                        <tr key={row.branch.id} className="align-top">
                          <td className="px-4 py-3">
                            <Badge>{row.branch.code}</Badge>
                          </td>
                          <td className="px-3 py-3 text-right tabular-nums text-slate-700">
                            {row.transactions}
                          </td>
                          <td className="px-3 py-3 text-right tabular-nums text-slate-700">
                            {row.payments}
                          </td>
                          <td className="px-3 py-3 text-right tabular-nums text-slate-700">
                            {centsToPesos(row.totalCents)}
                          </td>
                          <td className="px-3 py-3 text-right tabular-nums font-semibold text-emerald-700">
                            {centsToPesos(row.collectedCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <BalanceCell cents={row.outstandingCents} />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-panel shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                    <tr>
                      <th className="px-4 py-3">By payment method</th>
                      <th className="px-3 py-3 text-right">Payments</th>
                      <th className="px-4 py-3 text-right">Collected</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {collections.byMethod.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-4 py-4 text-center text-slate-400">
                          No payments recorded in this scope.
                        </td>
                      </tr>
                    ) : (
                      collections.byMethod.map((row) => (
                        <tr key={row.method ?? 'not-recorded'}>
                          <td className="px-4 py-3 text-slate-700">
                            {row.method ?? NOT_RECORDED}
                          </td>
                          <td className="px-3 py-3 text-right tabular-nums text-slate-700">
                            {row.payments}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums font-semibold text-emerald-700">
                            {centsToPesos(row.collectedCents)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Reconciliation: method totals sum to Collected, and branch totals sum to every figure
              above — All Branches equals LH1 + LH2 under identical filters. Release snapshots are
              checkpoints, never collections.
            </p>
          </>
        )}
      </div>

      {/* Payment & outstanding-balance monitoring (transaction-level). */}
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-semibold text-slate-700">Transactions & outstanding balances</h2>
          {list && total > 0 && (
            <p className="text-sm text-slate-500">
              Showing {showingFrom}–{showingTo} of {total} transaction{total === 1 ? '' : 's'} ·
              newest recorded first
            </p>
          )}
        </div>

        {loading && !list ? (
          <div className="flex items-center gap-3 py-6 text-sm text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading payments…
          </div>
        ) : !list || list.items.length === 0 ? (
          <Card className="mt-2 flex flex-col items-center p-6 text-center">
            <Icon name="check" className="h-8 w-8 text-emerald-400" />
            <p className="mt-2 font-medium text-slate-700">No transactions match these filters.</p>
            <p className="mt-1 text-sm text-slate-500">
              {hasListFilters
                ? 'Clear a filter to widen the search.'
                : 'Recorded payments will appear here once transactions are received.'}
            </p>
          </Card>
        ) : (
          <>
            {/* Desktop table */}
            <div className="mt-2 overflow-x-auto rounded-2xl border border-slate-200 bg-panel shadow-sm">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                  <tr>
                    <th className="px-4 py-3">Transaction</th>
                    <th className="px-4 py-3">Branch</th>
                    <th className="px-3 py-3 text-right">Total</th>
                    <th className="px-3 py-3 text-right">Paid</th>
                    <th className="px-3 py-3 text-right">Balance</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Method</th>
                    <th className="px-4 py-3">Recorded</th>
                    <th className="px-4 py-3">Findings</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {list.items.map((row) => (
                    <tr key={row.id} className="align-top hover:bg-slate-100/70">
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setHistoryRef(row.number)}
                          className="font-semibold text-indigo-600 underline-offset-2 hover:underline"
                          title="Open payment history"
                        >
                          {row.number}
                        </button>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {row.customerName} · {row.workflowStatusLabel}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <Badge>{row.branch.code}</Badge>
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-slate-700">
                        {centsToPesos(row.totalCents)}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-slate-700">
                        {centsToPesos(row.amountPaidCents)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <BalanceCell cents={row.outstandingCents} />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={row.paymentStatus} />
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {row.paymentMethod ?? (
                          <span className="italic text-slate-400">Not recorded</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <span className="block">{formatWhen(row.paymentRecordedAt)}</span>
                        <span className="mt-0.5 block text-xs text-slate-500">
                          {row.recordedBy ? row.recordedBy.name : NOT_RECORDED}
                        </span>
                      </td>
                      <td className="max-w-72 px-4 py-3">
                        <FlagBadges flags={row.flags} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>


            {/* Deterministic pagination */}
            {total > PAGE_SIZE && (
              <div className="mt-3 flex items-center justify-between">
                <Button
                  variant="secondary"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-500">
                  Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
                </span>
                <Button
                  variant="secondary"
                  disabled={(page + 1) * PAGE_SIZE >= total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Payment history: loading / error / recorded events + release checkpoint */}
      {historyRef && !history && !historyError && (
        <Modal title={`Payment history ${historyRef}`} onClose={() => setHistoryRef(null)}>
          <div className="flex items-center justify-center gap-3 py-8 text-sm text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading payment history…
          </div>
        </Modal>
      )}
      {historyRef && historyError && (
        <Modal title={`Payment history ${historyRef}`} onClose={() => setHistoryRef(null)}>
          <ErrorBanner message={historyError} />
        </Modal>
      )}
      {historyRef && history && (
        <Modal
          title={`Payment history — ${history.transaction.number}`}
          onClose={() => setHistoryRef(null)}
        >
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
              <Badge>{history.transaction.branch.code}</Badge>
              <Badge tone="indigo">{history.transaction.workflowStatusLabel}</Badge>
              <StatusBadge status={history.transaction.paymentStatus} />
              <span>{history.transaction.customerName}</span>
              <span className="text-slate-400">
                {history.transaction.fulfillmentMethod === 'DELIVERY' ? 'Delivery' : 'Pick-up'}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl border border-slate-200 p-3">
                <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">Total</p>
                <p className="mt-1 text-lg font-bold tabular-nums text-slate-900">
                  {centsToPesos(history.transaction.totalCents)}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">Paid</p>
                <p className="mt-1 text-lg font-bold tabular-nums text-emerald-700">
                  {centsToPesos(history.transaction.amountPaidCents)}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
                  Balance
                </p>
                <p className="mt-1 text-lg font-bold">
                  <BalanceCell cents={history.transaction.outstandingCents} />
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-slate-700">Recorded payments</h3>
              <p className="mt-0.5 text-xs text-slate-500">
                Payments are recorded at receiving; this model holds no later payment events.
              </p>
              {history.payments.length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">
                  No payment is recorded for this transaction.
                </p>
              ) : (
                <ol className="mt-2 space-y-3">
                  {history.payments.map((payment) => (
                    <li key={payment.id} className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                      <p className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-semibold text-slate-800">
                          {centsToPesos(payment.cents)} · {payment.method ?? 'Method not recorded'}
                        </span>
                        <span className="text-xs text-slate-500">{formatWhen(payment.at)}</span>
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        Recorded by {payment.recordedBy ? payment.recordedBy.name : null}
                        {payment.recordedBy ? null : <span className="italic">Not recorded</span>} ·
                        source: {payment.source}
                      </p>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            {history.release && (
              <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-3 text-sm text-indigo-900">
                <p className="font-semibold">Checkpoint at release</p>
                <p className="mt-1 tabular-nums">
                  {formatWhen(history.release.at)} · snapshot{' '}
                  {history.release.paymentStatus} · {centsToPesos(history.release.amountPaidCents)}{' '}
                  of {centsToPesos(history.release.totalCents)}
                  {history.release.releasedBy
                    ? ` · released by ${history.release.releasedBy.name}`
                    : ' · released by not recorded'}
                </p>
                <p className="mt-1 text-xs text-indigo-700">
                  Immutable snapshot of payment state at release — never counted as a collection.
                </p>
              </div>
            )}

            <div>
              <h3 className="text-sm font-semibold text-slate-700">Findings</h3>
              <div className="mt-2">
                <FlagBadges flags={history.transaction.flags} />
              </div>
            </div>

            <div className="border-t border-slate-100 pt-3">
              <Link
                to={`/admin/monitoring?q=${encodeURIComponent(history.transaction.number)}&detail=${history.transaction.id}`}
                className="text-sm font-semibold text-indigo-600 underline-offset-2 hover:underline"
              >
                Open full transaction detail in Transaction Monitoring →
              </Link>
            </div>
          </div>
        </Modal>
      )}
    </section>
  );
}
